import { Button, Card, Link, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import { Box } from '@mui/system';
import { ComposableMap, Geographies, Geography } from 'react-simple-maps';
import Mapdata from "./Map/MapData.json";
import { Link as RRLink } from "react-router-dom";

const HeroBox = styled((props) => (
    <Box component="section" {...props} />
))(({ theme }) => ({
    position: "relative",
    overflow: "hidden",
    background: "linear-gradient(to bottom, #D2E2FD 72%, #B9CFFD 100%)",
    padding: "9.5rem 0 2.5rem 0",
    [theme.breakpoints.up("md")]: {
        paddingTop: "11.5rem",
        paddingBottom: "8rem",
        clipPath: "polygon(0 0, 100% 0, 100% calc(100% - 3rem), 0 100%)",
    },
    [theme.breakpoints.up("xl")]: {
        clipPath: "none",
    }
}));

const HeroRow = styled(Box)(
    ({ theme }) => ({
        display: "flex",
        flexWrap: "wrap",
        justifyContent: "space-between",
        padding: "0 4vw",
    })
);

const HeroColumn = styled((props) => (
    <Box {...props} />
))(({ theme }) => ({
    flexShrink: 0,
    width: "100%",
    display: "flex",
    flexDirection: "column",
    position: "relative",
    [theme.breakpoints.up("md")]: {
        width: "48%",
    },
}));

const HeroTitle = styled((props) => (
    <Typography variant="h1" {...props} />
))(({ theme }) => ({
    fontWeight: 700,
    fontFamily: "Roboto Condensed",
    fontSize: "4.5rem",
    margin: theme.spacing(1, 0, 3, 0),
    lineHeight: 1.0625,
    [theme.breakpoints.up("sm")]: {
        fontSize: "6.2vw"
    },
    [theme.breakpoints.up("md")]: {
        fontSize: "5.1vw",
    },
}));

const HeroBody = styled((props) => (
    <Typography variant="body1" {...props} />
))(({ theme }) => ({
    fontWeight: 500,
    fontFamily: "inter",
    fontSize: "1.3rem",
    lineHeight: "1.44",
    marginBottom: theme.spacing(3),
    [theme.breakpoints.up("sm")]: {
        fontSize: "1.4rem",
    },
    [theme.breakpoints.up("md")]: {
        fontSize: "1.7vw",
    },
    [theme.breakpoints.up("lg")]: {
        fontSize: "1.5rem",
    },
    [theme.breakpoints.up("xl")]: {
        fontSize: "1.6rem",
    },
}));

const HeroButtonBox = styled(Box)(
    ({ theme }) => ({
        display: "flex",
        flexDirection: "column",
        [theme.breakpoints.up("xl")]: {
            flexDirection: "row",
        },
    })
);

const HeroButton = styled((props) => (
    <Button variant="contained" {...props} />
))(({ theme }) => ({
    flexGrow: 1,
    margin: theme.spacing(1),
    padding: theme.spacing(1.7, 0),
    borderRadius: "3rem",
    fontFamily: "montserrat",
    fontWeight: 800,
    fontSize: "1.5rem",
}));

const HeroMapBox = styled((props) => (
    <Card elevation={5} component={RRLink} to="/network" {...props} />
))(({ theme }) => ({
    transition: theme.transitions.create("transform"),
    cursor: "pointer",
    backgroundColor: "#497fc9",
    margin: theme.spacing(5, 0, 5, 2),
    "&:hover": {
        transform: "scale(1.03)",
    },
    position: "relative",
    overflow: "visible",
    "::after": {
        content: "'👆'",
        position: "absolute",
        left: "36%",
        bottom: 0,
        fontSize: "10rem",
        transform: "rotate(-27deg) translate(-25%, 30%)",
    },
    [theme.breakpoints.up("md")]: {
        margin: theme.spacing(3, 0, 5, 2),
    },
}));

const HeroMap = (props) => {
    return (
        <HeroMapBox>
            <ComposableMap
                width={700}
                height={420}
                style={{ width: "100%" }}
                projectionConfig={{ center: [2, 8] }}
            >
                <Geographies geography={Mapdata}>
                    {({ geographies }) => (
                        geographies.map((geo) => (
                            <Geography
                                key={geo.rsmKey}
                                geography={geo}
                                style={{
                                    default: { outline: "none", fill: "#f4f0f0", stroke: "#f4f0f0" },
                                    hover: { outline: "none", fill: "#f4f0f0", stroke: "#f4f0f0" },
                                    pressed: { outline: "none", fill: "#f4f0f0", stroke: "#f4f0f0" },
                                }}
                            />
                        ))
                    )}
                </Geographies>
            </ComposableMap>
        </HeroMapBox>
    )
};

export default function Hero(props) {
    return (
        <HeroBox>
            <HeroRow>
                <HeroColumn sx={{ justifyContent: "center" }}>
                    <HeroTitle>
                        Geolocate CDN edges <span style={{ whiteSpace: "nowrap" }}>at the</span> <span style={{ whiteSpace: "nowrap" }}>city level</span>
                    </HeroTitle>
                    <HeroBody>
                        LocEdge is a geolocation tool for CDN edge servers.
                        Unlike existing GeoIP databases, LocEdge leverages geographic hints in HTTP headers to determine the physical location of 
                        an endpoint and achieves much higher accuracy. <Link component={RRLink} to="/about">Learn more</Link>
                    </HeroBody>
                    <HeroButtonBox>
                        <HeroButton sx={{
                            backgroundColor: "#852B90",
                            "&:hover": {
                                backgroundColor: "#702479",
                            },
                        }} component={RRLink} to="/demo">
                            Try Demo
                        </HeroButton>
                        <HeroButton sx={{
                            backgroundColor: "#303030",
                            "&:hover": {
                                backgroundColor: "#1a1a1a",
                            },
                        }} component={RRLink} to="/network">
                            Explore CDN Locations
                        </HeroButton>
                    </HeroButtonBox>
                </HeroColumn>
                <HeroColumn sx={{ maxWidth: { xs: "96%", sm: "80%", md: "100%" } }}>
                    <HeroMap />
                </HeroColumn>
            </HeroRow>
        </HeroBox>
    );
};